/**
 * Regelversion 9: Demografie aus einer Bevölkerungsrechnung statt fester Mehrkosten.
 *
 * Bis Version 8 wurden Rente und Pflege jeden Monat um einen festen Betrag teurer, egal was die
 * Regierung tat und welches Jahr war. Jetzt gibt es eine einfache Bevölkerungsrechnung:
 *  - Altenquotient: Menschen ab 65 je 100 im Erwerbsalter (20 bis 64). Er steigt, solange die
 *    Babyboomer in Rente gehen (stark bis Mitte der 2030er, danach langsamer).
 *  - Zuwanderung: Netto-Zuwanderung in Mio pro Jahr. Sie verjüngt die Bevölkerung und bringt Arbeitskräfte.
 *  - Geburten: Kinder je Frau. Wirken auf die Bevölkerung, entlasten die Rente aber erst nach 20 Jahren.
 *  - Rentenalter: Eine Kopplung an die Lebenserwartung senkt den wirksamen Altenquotienten, früher in Rente hebt ihn.
 *  - Erwerbspersonen: Wer arbeitet. Sinkt mit dem Renteneintritt der Babyboomer, steigt durch Zuwanderung,
 *    Kitas und Anreize für mehr Arbeit, sinkt durch Auswanderung von Fachkräften (Version 8).
 * Daraus folgen die Mehrkosten durch Alterung im Haushalt und der Druck bei Rente, Pflege und Fachkräften.
 *
 * Außerdem: Das Problem „Rente nach 2031“ taucht nach 2031 nicht mehr neu auf. Läuft es noch, geht es 2032
 * in der allgemeinen Rentenfrage („Rentenlücke“) auf.
 */
import { S, log } from './state';
import { clamp, dateYear, de } from './util';
import { since } from './version';
import { DI, DM, on } from '../content/settings';
import { TPL } from '../content/issues';

export type Lever = { id: number; mig?: number; tfr?: number; part?: number; ra?: number; once?: number; fin?: number };
export type Pop = {
  n: number; oaq: number; ra: number; once: number; wa: number; part: number; tfr: number; mig: number;
  lv: Record<string, Lever>; hist: { y: number; n: number; q: number; wa: number; mig: number }[]; y: number; merged?: boolean;
};

export const POP0 = 84.5, OAQ0 = 37.5, WA0 = 46.2, TFR0 = 1.35, MIG0 = .35;
/** Ab diesem Jahr ist „Rente nach 2031“ kein eigenes Problem mehr. */
export const RENTE67_END = 2032;

export const demoOn = (): boolean => since(9) && on('demo');

/** Grundtrend des Altenquotienten pro Jahr (Babyboomer gehen in Rente, danach flacht es ab). */
const ageRate = (y: number): number => (y < 2036 ? .9 : y < 2040 ? .55 : y < 2050 ? .25 : .1);
/** Grundtrend der Erwerbspersonen in Mio pro Jahr. */
const waRate = (y: number): number => (y < 2036 ? -.4 : y < 2045 ? -.15 : -.05);

/** Wirkung eines Gesetzes auf die Bevölkerung. `ra`: Senkung des wirksamen Altenquotienten pro Jahr, `once`: einmalig (eingeschliffen). */
const LAWS: Record<string, (p: any) => Omit<Lever, 'id'>> = {
  'Fachkräftesicherungsgesetz': (p) => ({ mig: [0, .08, .18][p.ein ?? 1], part: (p.wb || 0) * .05 + (p.ar ? .08 : 0) }),
  'Migrationspaket': (p) => ({ mig: [0, -.03, -.1][p.gk ?? 1], part: p.ae ? .05 : 0 }),
  'Asylverfahren in Drittstaaten': (p) => ({ mig: (p.ds ? -.08 : 0) + (p.lz ? -.03 : 0) }),
  'Staatsangehörigkeitsreform': (p) => ({ mig: [-.04, 0, .04][p.r ?? 1] }),
  'Familienentlastungsgesetz': (p) => ({ tfr: (p.kg || 0) * .002 + [-.03, 0, .04][p.eg ?? 1], part: [-.02, 0, .03][p.eg ?? 1] }),
  'Kita-Ausbaugesetz': (p) => ({ tfr: (p.bm || 0) * .01 + (p.fr ? .03 : 0), part: (p.bm || 0) * .04 + (p.fr ? .03 : 0) }),
  'Arbeitsanreizgesetz': (p) => ({ part: (p.ue ? .03 : 0) + (p.tz || 0) * .03 + [.04, 0, -.01][p.hz ?? 1] }),
  'Rentenreform': (p) => ({ ra: [0, .25, 0][p.a ?? 0], once: p.a === 2 ? -2.5 : 0, fin: (p.ak || 0) * .02 + [.06, 0, -.08][p.n ?? 1] }),
  'Rentenpaket der Kommission': (p) => ({ ra: [0, .2, .35][p.al ?? 1], once: p.r63 ? .8 : 0, fin: (p.nf ? .12 : 0) + (p.kap || 0) * .04 }),
  'Rentenversicherung für alle': (p) => ({ fin: (p.bm ? .1 : 0) + (p.sb ? .08 : 0) }),
  'Erwerbstätigenversicherung': (p) => ({ fin: (p.alle ? .15 : 0) - [0, .06, .15][p.ni ?? 1] }),
  'Pflegevollversicherung': () => ({ fin: -.15 }),
  'Anerkennungsoffensive': (p) => ({ mig: (p.an ? .04 : 0) + (p.wv ? .05 : 0), part: (p.sp || 0) * .03 }),
  'Kita-Qualitätsgesetz': (p) => ({ tfr: p.rp ? .01 : 0, part: (p.rp ? .05 : 0) + (p.fk || 0) * .02 }),
  'Rückführung und Integration aus einer Hand': (p) => ({ mig: p.rz ? -.04 : 0, part: p.aa ? .05 : 0 }),
  'Pflegekompetenzgesetz': (p) => ({ part: p.pa ? .03 : 0 }),
  'Betriebsrenten-Stärkungsgesetz': (p) => ({ fin: (p.om ? .04 : 0) + (p.rv ? .03 : 0) }),
};

const titleOf = (L: any): string => { try { return L.iss ? TPL(L.iss, L.tpl || 0).t : L.t; } catch { return L.t; } };
/** Hat ein Gesetz demografische Wirkung? (Für den Hinweis im Entwurf.) */
export const demoLaw = (t: string, p: any): Omit<Lever, 'id'> | null => (LAWS[t] ? LAWS[t](p || {}) : null);

/** Bevölkerung zu Beginn der Rechnung, passend zum Jahr (auch beim Umstieg mitten in einer Karriere). */
function initPop(): Pop {
  const Y = dateYear(S.m); let oaq = OAQ0, wa = WA0;
  for (let y = 2030; y < Y; y++) { oaq += ageRate(y); wa += waRate(y) + MIG0 * .55; }
  const P: Pop = { n: POP0, oaq, ra: 0, once: 0, wa, part: 0, tfr: TFR0, mig: MIG0, lv: {}, hist: [], y: Y };
  S.pop = P;
  // Bereits geltende Gesetze nachtragen
  for (const L of S.laws || []) if (!L.rep && (L.stage === 'kraft' || L.stage === 'done')) demoEnact(L, true);
  return P;
}

export const pop = (): Pop | null => (demoOn() ? (S.pop as Pop) || initPop() : null);

/** Summe der Hebel aus geltenden Gesetzen. */
export function levers(): Required<Omit<Lever, 'id'>> {
  const P = S.pop as Pop | undefined, r = { mig: 0, tfr: 0, part: 0, ra: 0, once: 0, fin: 0 };
  for (const k in P?.lv || {}) { const l: any = P!.lv[k]; for (const x in r) (r as any)[x] += l[x] || 0; }
  r.mig = clamp(r.mig, -.35, .5); r.tfr = clamp(r.tfr, -.15, .35); r.part = clamp(r.part, -.3, 1.2); r.ra = clamp(r.ra, 0, .4); r.fin = clamp(r.fin, -.4, .5);
  return r;
}

/** Netto-Zuwanderung in Mio pro Jahr (inkl. Auswanderung von Fachkräften). */
export const netMig = (): number => MIG0 + levers().mig - ((S.flightA || 0) * .025);
/** Kinder je Frau. */
export const tfrNow = (): number => TFR0 + levers().tfr;
/** Wirksamer Altenquotient: mit Rentenalter-Effekten. */
export const oaqEff = (): number => { const P = S.pop as Pop; return P ? P.oaq - P.ra - P.once : OAQ0; };

/** Beim Inkrafttreten eines Gesetzes. `quiet`: beim Nachtragen ohne Meldung. */
export function demoEnact(L: any, quiet = false) {
  if (!demoOn()) return;
  const t = titleOf(L), f = LAWS[t]; if (!f) return;
  const P = (S.pop as Pop) || (quiet ? null : pop()); if (!P) return;
  P.lv[t] = { id: L.id, ...f(L.p || {}) };
}
/** Gesetz aufgehoben oder in Karlsruhe gekippt. */
export function demoRevert(L: any) {
  const P = S.pop as Pop | undefined; if (!since(9) || !P) return;
  const t = titleOf(L); if (P.lv[t]?.id === L.id) delete P.lv[t];
}

/** Monatlich, statt der festen Mehrkosten aus Version 1 bis 8. */
export function demoTick() {
  const P = pop(); if (!P) return;
  const Y = dateYear(S.m), lv = levers(), mig = netMig();
  // Altenquotient: Grundtrend, Zuwanderung verjüngt, Geburten wirken erst nach 20 Jahren (hier nur leicht)
  const dq = ageRate(Y) - (mig - MIG0) * P.oaq / 49 - Math.max(0, lv.tfr) * (Y >= 2050 ? .5 : 0);
  P.oaq += dq / 12;
  // Rentenalter: Kopplung wirkt jedes Jahr, Einmaleffekte schleifen sich ein
  P.ra += lv.ra / 12;
  const dOnce = clamp(lv.once - P.once, -.04, .04); P.once += dOnce;
  // Erwerbspersonen
  const partT = lv.part, dPart = clamp(partT - P.part, -.02, .02);
  P.part += dPart;
  const dwa = waRate(Y) + mig * .55 + lv.ra * .4;
  P.wa += dwa / 12 + dPart;
  // Bevölkerung: Geburten minus Sterbefälle plus Zuwanderung
  P.tfr = tfrNow(); P.mig = mig;
  P.n += (P.tfr * .5 - 1 - (Y - 2030) * .005 + mig) / 12;
  // Mehrkosten im Haushalt folgen dem wirksamen Altenquotienten
  const target = Math.max(-4, (oaqEff() - OAQ0) * 1.25 * [.6, 1, 1.4][DI()] * (1 - lv.fin));
  S.demoCost += (target - S.demoCost) * .1;
  // Druck auf Rente, Pflege, Fachkräfte
  const qEff = ageRate(Y) - (mig - MIG0) * P.oaq / 49 - lv.ra - dOnce * 12;
  const push = (k: string, v: number) => { const st = S.issues[k]; if (st) st.sev = clamp(st.sev + v, 0, 100); };
  push('rente', clamp(.04 * DM() * qEff / .9 * (1 - Math.min(.6, Math.max(0, lv.fin))), -.03, .08));
  push('pflege', clamp(.04 * DM() * ageRate(Y) / .9, 0, .08));
  if (S.issues.fach?.on) push('fach', clamp(-.03 * (dwa + dPart * 12) / .4, -.06, .06));
  // Jahreswerte für die Grafik
  if (Y !== P.y) { P.y = Y; P.hist.push({ y: Y, n: +P.n.toFixed(2), q: +oaqEff().toFixed(1), wa: +P.wa.toFixed(2), mig: +mig.toFixed(2) }); if (P.hist.length > 40) P.hist.shift(); }
  rente67Tick(Y);
}

/** „Rente nach 2031“: ab 2032 kein eigenes Problem mehr, die Debatte geht in der Rentenlücke auf. */
function rente67Tick(Y: number) {
  const st = S.issues.rente67, R = S.issues.rente; if (!st || !R || Y < RENTE67_END) return;
  if (st.on && !st.law) {
    st.on = false; st.solved = S.m; st.sev = 0;
    R.on = true; R.solved = null; R.sev = clamp(Math.max(R.sev, 30) + 4 * (1 - (st.cut || 0)), 0, 100);
    log('Die Haltelinie beim Rentenniveau ist ausgelaufen. Die Debatte über die Rente nach 2031 geht in der allgemeinen Rentenfrage auf.', 'bad');
  }
}

/** Darf ein Problem (wieder) auftauchen? */
export const relevant9 = (id: string): boolean => !(id === 'rente67' && since(9) && dateYear(S.m) >= RENTE67_END - 1);

/** Text für Rente, Pflege und Fachkräfte passend zur Bevölkerung. */
export function demoDesc(id: string): string | null {
  const P = demoOn() ? (S.pop as Pop | undefined) : null; if (!P) return null;
  const q = oaqEff(), m = netMig();
  if (id === 'rente') return `Auf 100 Menschen im Erwerbsalter kommen ${de(q, 0)} über 65${P.ra + P.once > .3 ? ' (mit eurer Rentenreform gerechnet)' : ''}. ${q > OAQ0 + 3 ? 'Beiträge und Bundeszuschuss steigen, das Rentenniveau droht zu sinken.' : 'Noch hält das System, aber die Babyboomer gehen in Rente.'}`;
  if (id === 'pflege') return `${de(P.n, 1)} Millionen Menschen leben in Deutschland, immer mehr von ihnen sind hochbetagt. Pflegeheime melden Insolvenz, die Eigenanteile steigen.`;
  if (id === 'fach') return `${de(P.wa, 1)} Millionen Erwerbspersonen, Netto-Zuwanderung ${de(m * 1000, 0)} Tausend im Jahr. Pflege, Handwerk und IT finden kaum Personal.`;
  return null;
}
